import React, { useState, useRef, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Avatar,
  Typography,
  IconButton,
  MenuItem,
  ListItemIcon,
  Divider,
  Paper,
  Badge,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,

  useMediaQuery,
} from "@mui/material";
import { Icon } from "../icons";
import { COLORS, FONT_FAMILY } from "../constants/color";
import profilePhoto from "../assets/icons/watermark.png";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/authContext";
import UserProfileSidebar from "../components/UserProfileSidebar";
import { useTheme } from "@mui/material";
import axios from "axios";
import { url } from "../constants/url";
import { useFlowToggle } from "../context/flowToggleContext";
const Header = ({ user, toggleSidebar, sidebarOpen }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [profileSidebarOpen, setProfileSidebarOpen] = useState(false);
  const [userCredits, setUserCredits] = useState(null);
  const profileRef = useRef(null);
  const navigate = useNavigate();
  const { logout, userData, canAccessTechnicalFlow } = useAuth();
  const { isTechnicalFlow, toggleFlow } = useFlowToggle();

  // Handle toggle flow with navigation
  const handleToggleFlow = () => {
    const newFlow = !isTechnicalFlow;
    toggleFlow();
    // Navigate to appropriate screen based on new flow and user role
    if (newFlow) {
      // For Admin and SuperAdmin - go to Add New Project
      // For Engineer - go to Project Folder (In Progress)
      // For other roles - go to Technical Dashboard
      if (userData?.role === 'ADMIN' || userData?.role === 'SUPERADMIN') {
        navigate('/technical/dashboard');
      } else if (userData?.role === 'ENGINEER') {
        navigate('/technical/projects/in-progress');
      } else {
        navigate('/technical/dashboard');
      }
    } else {
      navigate('/dashboard');
    }
  };

  // Add media query inside Header
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  // Fetch user credits for client role
  useEffect(() => {
    const fetchUserCredits = async () => {
      const currentUser = userData || user;
      console.log('Credits fetch - Current user:', currentUser);
      console.log('Credits fetch - User role:', currentUser?.role);
      console.log('Credits fetch - User ID:', currentUser?._id);

      if (currentUser?.role === 'CLIENT' && currentUser?._id) {
        try {
          const token = localStorage.getItem('token');
          console.log('Credits fetch - Token exists:', !!token);
          console.log('Credits fetch - Making API call to:', `${url}/api/credits/data/${currentUser._id}`);

          const response = await axios.get(
            `${url}/api/credits/data/${currentUser._id}`,
            {
              headers: {
                Authorization: token ? `Bearer ${token}` : undefined,
              },
            }
          );

          console.log('Credits fetch - API response:', response.data);
          console.log('Credits fetch - Full response structure:', JSON.stringify(response.data, null, 2));

          if (response.data && response.data.creditSummary) {
            setUserCredits({
              normal: response.data.creditSummary,
              outstation: response.data.outstationCreditSummary || { availableCredits: 0 }
            });
            console.log('Credits fetch - Credits set:', response.data);
          } else {
            console.log('Credits fetch - No creditSummary in response');
          }
        } catch (error) {
          console.error('Credits fetch - Error fetching user credits:', error);
          console.error('Credits fetch - Error details:', error.response?.data);
        }
      } else {
        console.log('Credits fetch - Not a client or no user ID, skipping');
      }
    };

    fetchUserCredits();

    const handleCreditsUpdate = () => {
      fetchUserCredits();
    };

    window.addEventListener("creditsUpdated", handleCreditsUpdate);
    return () => {
      window.removeEventListener("creditsUpdated", handleCreditsUpdate);
    };
  }, [userData, user]);

  const handleProfileClick = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleClose = () => {
    setIsDropdownOpen(false);
  };

  // Handle opening profile sidebar
  const handleOpenProfileSidebar = () => {
    setProfileSidebarOpen(true);
    handleClose();
  };

  // Handle closing profile sidebar
  const handleCloseProfileSidebar = () => {
    setProfileSidebarOpen(false);
  };

  // Handle logout confirmation
  const handleLogoutClick = () => {
    setLogoutDialogOpen(true);
    handleClose();
  };

  // Confirm logout
  const confirmLogout = async () => {
    try {
      await logout();
      // Redirect to login page
      navigate("/login");
      setLogoutDialogOpen(false);
    } catch (error) {
      console.error("Logout error:", error);
      // Still redirect to login even if there's an error
      navigate("/login");
      setLogoutDialogOpen(false);
    }
  };

  // Cancel logout
  const cancelLogout = () => {
    setLogoutDialogOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Get user info from auth context or props
  const currentUser = userData || user;
  const userName = currentUser?.name || currentUser?.firstName || "User";
  const userRole = currentUser?.role || "Engineer";
  const userEmail = currentUser?.email || currentUser?.phone || "";

  // Debug credits display
  console.log('Render - User role:', userRole);
  console.log('Render - UserCredits:', userCredits);
  console.log('Render - Should show credits:', userRole === "CLIENT" && userCredits);

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: COLORS.BACKGROUND,
          borderBottom: `1px solid ${COLORS.BORDER}`,
          zIndex: 1100,
          // On desktop: adjust width based on sidebar
          width: isMobile
            ? "100%"
            : sidebarOpen
              ? "calc(100% - 240px)"
              : "100%",
          // On desktop: shift position based on sidebar
          left: isMobile ? 0 : sidebarOpen ? "240px" : 0,
          transition: theme.transitions.create(['width', 'left'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Toolbar
          sx={{
            minHeight: 64,
            px: { xs: 2, sm: 3 },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {/* LEFT SIDE: Menu button and Search */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexGrow: 1,
              gap: 2,
            }}
          >
            {/* Menu Button - Only show on mobile */}


            {/* Menu Button - Show on mobile/tablet (below lg) */}
            <IconButton
              onClick={toggleSidebar}
              sx={{
                display: { xs: 'flex', lg: 'none' },
                color: COLORS.TEXT,
                '&:hover': {
                  backgroundColor: 'rgba(101, 45, 144, 0.1)',
                },
              }}
            >
              <Icon name="menu" color={COLORS.PRIMARY} />
            </IconButton>



            {/* SEARCH BOX */}
            {/* <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexGrow: 0,
                width: { xs: 180, sm: 300, md: 350 },
                px: 2,
                py: 0.7,
                borderRadius: 1.5,
                border: `1px solid ${COLORS.PRIMARY}`,
                backgroundColor: COLORS.BACKGROUND,
                color: COLORS.PRIMARY,
              }}
            >
              <Icon name="search" sx={{ fontSize: 18, color:"currentColor" }} />
              <InputBase
                placeholder="Search..."
                sx={{
                  flex: 1,
                  fontFamily: FONT_FAMILY,
                  ...TYPOGRAPHY.BODY_STANDARD,
                  color: COLORS.TEXT,
                  ml: 1,
                }}
              />
            </Box> */}
          </Box>

          {/* RIGHT SIDE: Icons and Profile */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, sm: 1.5 },
              ml: "auto",
            }}
          >
            {/* Credits Display - Only for CLIENT role */}
            {userRole === "CLIENT" && userCredits && currentUser?.clientRole?.roleType !== 'Individual' && currentUser?.clientRole !== 'Individual' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Normal Credits */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1.5,
                    py: 0.8,
                    borderRadius: "8px",
                    backgroundColor: "rgba(106, 17, 203, 0.1)",
                    border: `1px solid ${COLORS.PRIMARY}`,
                  }}
                >
                  <Icon
                    name="check-circle"
                    color={COLORS.PRIMARY}
                    sx={{ fontSize: 16, }}
                  />
                  <Typography
                    sx={{
                      fontFamily: FONT_FAMILY,
                      fontSize: 13,
                      color: COLORS.PRIMARY,
                      fontWeight: 600,
                    }}
                  >
                    N: {userCredits?.normal?.availableCredits || 0}
                  </Typography>
                </Box>

                {/* Outstation Credits */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1.5,
                    py: 0.8,
                    borderRadius: "8px",
                    backgroundColor: "rgba(255, 152, 0, 0.1)",
                    border: "1px solid #FF9800",
                  }}
                >
                  <Icon
                    name="check-circle"
                    color="#FF9800"
                    sx={{ fontSize: 16 }}
                  />
                  <Typography
                    sx={{
                      fontFamily: FONT_FAMILY,
                      fontSize: 13,
                      color: "#E65100",
                      fontWeight: 600,
                    }}
                  >
                    O: {userCredits?.outstation?.availableCredits || 0}
                  </Typography>
                </Box>

                {/* Purchase Credits Button */}
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => navigate("/buy-credits")}
                  sx={{
                    background: "linear-gradient(135deg, #421A6E 0%, #652D90 100%)",
                    color: "#fff",
                    fontFamily: FONT_FAMILY,
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    px: 1.8,
                    py: 0.75,
                    borderRadius: "8px",
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    boxShadow: "0 3px 10px rgba(66, 26, 110, 0.35)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #2d1050 0%, #421A6E 100%)",
                      boxShadow: "0 5px 16px rgba(66, 26, 110, 0.45)",
                      transform: "translateY(-1px)",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  💳 Purchase Credits
                </Button>
              </Box>
            )}



            {/* Flow Toggle Switch - Only for Admin, SuperAdmin, and Engineer */}
            {canAccessTechnicalFlow() && (
              <Box
                onClick={handleToggleFlow}
                sx={{
                  display: { xs: "none", sm: "flex" },
                  alignItems: "center",
                  backgroundColor: COLORS.HOVER_BACKGROUND,
                  borderRadius: "24px",
                  p: "4px",
                  cursor: "pointer",
                  width: "fit-content",
                  border: `1px solid ${COLORS.BORDER}`,
                  transition: "all 0.3s ease",
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 0.8,
                    borderRadius: "20px",
                    backgroundColor: !isTechnicalFlow ? "#FFFFFF" : "transparent",
                    color: !isTechnicalFlow ? COLORS.SUCCESS : COLORS.MUTED_TEXT,
                    fontWeight: 700,
                    fontSize: 12,
                    fontFamily: FONT_FAMILY,
                    transition: "all 0.3s ease",
                    boxShadow: !isTechnicalFlow ? "0px 2px 8px rgba(0,0,0,0.08)" : "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  Main Portal
                </Box>
                <Box
                  sx={{
                    px: 2,
                    py: 0.8,
                    borderRadius: "20px",
                    backgroundColor: isTechnicalFlow ? "#FFFFFF" : "transparent",
                    color: isTechnicalFlow ? COLORS.SUCCESS : COLORS.MUTED_TEXT,
                    fontWeight: 700,
                    fontSize: 12,
                    fontFamily: FONT_FAMILY,
                    transition: "all 0.3s ease",
                    boxShadow: isTechnicalFlow ? "0px 2px 8px rgba(0,0,0,0.08)" : "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  Field Inspection
                </Box>
              </Box>
            )}

            {/* Technical Flow Header - Only show when in technical flow */}


            {/* Calendar Icon */}
            {!(userRole === "CLIENT" && (currentUser?.clientRole?.roleType === "Individual" || currentUser?.clientRole === "Individual")) && (
              <IconButton
                onClick={() => navigate(isTechnicalFlow ? "/technical/calendar" : "/calendar")}
                sx={{ display: { xs: "none", sm: "flex" } }}
              >
                <Badge color="error">
                  <Icon
                    name="calendar"
                    sx={{ fontSize: 20, color: COLORS.MUTED_TEXT }}
                  />
                </Badge>
              </IconButton>
            )}
            {/* Notifications Icon */}
            {/* <IconButton>
              <Badge badgeContent={5} color="error">
                <Icon
                  name="notification"
                  sx={{ fontSize: 20, color: COLORS.MUTED_TEXT }}
                />
              </Badge>
            </IconButton> */}

            {/* USER PILL */}
            <Box
              ref={profileRef}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.2,
                px: 1.5,
                py: 0.5,
                borderRadius: 20,
                backgroundColor: COLORS.PRIMARY,
                cursor: "pointer",
                position: "relative",
                "&:hover": {
                  backgroundColor: COLORS.SECONDARY,
                },
              }}
              onClick={handleProfileClick}
            >
              <Avatar
                src={profilePhoto}
                sx={{
                  width: 30,
                  height: 30,
                  bgcolor: COLORS.LIGHT_SECONDARY,
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {userName?.charAt(0)?.toUpperCase()}
              </Avatar>

              {/* User info - hidden on mobile */}
              <Box sx={{ display: { xs: "none", sm: "block" } }}>
                <Typography
                  sx={{
                    fontFamily: FONT_FAMILY,
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#FFFFFF",
                    lineHeight: "16px",
                  }}
                >
                  {userName}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: FONT_FAMILY,
                    fontSize: 11,
                    color: COLORS.LIGHT_SECONDARY,
                  }}
                >
                  {userRole}
                </Typography>
              </Box>

              {/* Dropdown arrow */}
              <Icon
                name={isDropdownOpen ? "expand-less" : "expand-more"}
                sx={{
                  fontSize: 16,
                  color: "#FFFFFF",
                  transition: "transform 0.2s",
                  transform: isDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                  display: { xs: "none", sm: "block" },
                }}
              />

              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <Paper
                  sx={{
                    position: "absolute",
                    top: "100%",
                    right: 0,
                    mt: 1,
                    minWidth: 200,
                    borderRadius: 1.5,
                    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                    overflow: "hidden",
                    zIndex: 1400,
                    border: `1px solid ${COLORS.BORDER}`,
                  }}
                  elevation={3}
                >
                  <Box
                    sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}
                  >
                    <Avatar src={profilePhoto} sx={{ width: 40, height: 40 }} />
                    <Box>
                      <Typography
                        sx={{
                          fontFamily: FONT_FAMILY,
                          fontSize: 14,
                          fontWeight: 600,
                          color: COLORS.TEXT,
                        }}
                      >
                        {userName}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: FONT_FAMILY,
                          fontSize: 12,
                          color: COLORS.MUTED_TEXT,
                          mt: 0.5,
                        }}
                      >
                        {userEmail}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider />

                  <MenuItem
                    onClick={handleOpenProfileSidebar}
                    sx={{ py: 1.5, px: 2 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Icon
                        name="profile"
                        sx={{ fontSize: 18, color: COLORS.PRIMARY }}
                      />
                    </ListItemIcon>
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        fontSize: 13,
                        color: COLORS.TEXT,
                      }}
                    >
                      View Profile
                    </Typography>
                  </MenuItem>

                  {userData?.role !== "CLIENT" && (
                    <MenuItem
                      onClick={() => {
                        handleClose();
                        navigate("/settings/general");
                      }}
                      sx={{ py: 1.5, px: 2 }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Icon
                          name="settings"
                          sx={{ fontSize: 18, color: COLORS.PRIMARY }}
                        />
                      </ListItemIcon>
                      <Typography
                        sx={{
                          fontFamily: FONT_FAMILY,
                          fontSize: 13,
                          color: COLORS.TEXT,
                        }}
                      >
                        Settings
                      </Typography>
                    </MenuItem>
                  )}

                  <Divider />

                  <MenuItem onClick={handleLogoutClick} sx={{ py: 1.5, px: 2 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Icon
                        name="logout"
                        sx={{ fontSize: 18, color: COLORS.ERROR }}
                      />
                    </ListItemIcon>
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        fontSize: 13,
                        color: COLORS.ERROR,
                      }}
                    >
                      Logout
                    </Typography>
                  </MenuItem>
                </Paper>
              )}
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={cancelLogout}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 300,
          },
        }}
      >
        <DialogTitle
          id="logout-dialog-title"
          sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}
        >
          Confirm Logout
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="logout-dialog-description"
            sx={{ fontFamily: FONT_FAMILY, color: COLORS.TEXT }}
          >
            Are you sure you want to logout? You will need to sign in again to
            access your account.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={cancelLogout}
            variant="outlined"
            sx={{
              fontFamily: FONT_FAMILY,
              textTransform: "none",
              color: COLORS.PRIMARY,
              borderColor: COLORS.PRIMARY,
              "&:hover": {
                borderColor: COLORS.SECONDARY,
                backgroundColor: "rgba(101, 45, 144, 0.04)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmLogout}
            variant="contained"
            sx={{
              fontFamily: FONT_FAMILY,
              textTransform: "none",
              backgroundColor: COLORS.ERROR,
              "&:hover": {
                backgroundColor: "#d32f2f",
              },
            }}
            autoFocus
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Profile Sidebar */}
      <UserProfileSidebar
        open={profileSidebarOpen}
        onClose={handleCloseProfileSidebar}
        user={currentUser}
      />
    </>
  );
};

export default Header;
